import type { MessageKey } from "../i18n/keys";
import type { IconName } from "../ui/Icon";

/** One destination: a module's nav entry as "module:entry", shown here under `labelKey` when the job's word differs from the module's. */
export type MenuItem = readonly [entry: string, labelKey?: MessageKey];

export interface MenuGroup {
  id: string;
  labelKey: MessageKey;
  icon: IconName;
  items: readonly MenuItem[];
}

/**
 * The staff menu, in one place, grouped by the job and not by the backend module. A module still owns what an entry is (its path,
 * permission and license); this says only where it sits and what it is called there. Every entry of every module appears exactly
 * once, here or in SETTINGS_MENU (menu.test.ts), and paths never change, so old links keep working.
 */
export const MENU: readonly MenuGroup[] = [
  { id: "members", labelKey: "menu.members", icon: "users", items: [
    ["members:households", "menu.memberDirectory"],
    ["members:member-search"],
    ["portal:portal-member-requests", "menu.requests"],
    ["lodging:requests", "menu.stayRequests"],
    ["portal:portal-access", "menu.portalAccess"],
  ] },
  { id: "golf", labelKey: "menu.golf", icon: "golf", items: [
    ["teesheet:sheet"],
    ["teesheet:club-course", "menu.courseInfo"],
  ] },
  { id: "dining-events", labelKey: "menu.diningEvents", icon: "calendar", items: [
    ["pos:pos-register"],
    ["pos:pos-kitchen"],
    ["events:dining", "menu.reservations"],
    ["events:dining-requests"],
    ["events:events"],
    ["events:spaces"],
    ["pos:pos-cash"],
    ["pos:pos-day-close"],
    ["pos:pos-recovery", "menu.paymentsToReview"],
  ] },
  { id: "rooms", labelKey: "menu.rooms", icon: "house", items: [
    ["lodging:availability"],
    ["lodging:stays"],
    ["lodging:housekeeping"],
    ["lodging:housekeeping-tasks"],
    ["lodging:exceptions", "menu.needsAttention"],
  ] },
  { id: "guests", labelKey: "menu.guests", icon: "flag", items: [
    ["frontdesk:desk"],
    ["guests:arrivals"],
    ["guests:visits"],
    ["guests:calendar"],
    ["guests:people"],
  ] },
  { id: "finance", labelKey: "menu.finance", icon: "card", items: [
    ["billing:billing-accounts", "menu.memberAccounts"],
    ["accounting:accounting-runs"],
    ["accounting:accounting-minimum", "menu.minimums"],
    ["accounting:accounting-statements", "menu.statements"],
    ["expenses:expenses-entries", "menu.billsExpenses"],
    ["expenses:expenses-vendors"],
    ["pos:pos-supplier-invoices"],
    ["ledger:ledger-deposits", "menu.bankDeposits"],
    ["ledger:ledger-journals"],
    ["ledger:ledger-trial-balance"],
    ["ledger:ledger-gl-detail", "menu.generalLedger"],
    ["ledger:ledger-inquiry", "menu.accountActivity"],
  ] },
  { id: "inventory", labelKey: "menu.inventory", icon: "table", items: [
    ["pos:pos-items"],
    ["pos:pos-inventory"],
    ["pos:pos-counts"],
    ["pos:pos-purchasing"],
  ] },
  { id: "communications", labelKey: "menu.communications", icon: "message", items: [
    ["communications:messages"],
    ["communications:texts"],
    ["communications:log"],
    ["notifications:notifications-outbox"],
  ] },
  { id: "insights", labelKey: "menu.insights", icon: "chart", items: [
    ["reports:reports-dashboard", "menu.dashboards"],
    ["reports:reports-library"],
    ["reports:reports-builder"],
    ["expenses:expenses-reports"],
  ] },
  { id: "files", labelKey: "files.title", icon: "note", items: [["documents:files"]] },
];

/** The sections of the single Settings page (/settings). Setup screens leave the daily menu and come here. */
export const SETTINGS_MENU: readonly MenuGroup[] = [
  { id: "club", labelKey: "menu.settings.club", icon: "building", items: [
    ["admin:settings"],
    ["admin:wording", "menu.customText"],
    ["portal:portal-settings"],
    ["portal:portal-app"],
    ["frontdesk:desks"],
  ] },
  { id: "people", labelKey: "menu.settings.people", icon: "users", items: [
    ["admin:users"],
    ["admin:roles"],
    ["portal:portal-staff-views", "menu.staffAccessLog"],
    ["admin:audit"],
  ] },
  { id: "members", labelKey: "menu.settings.members", icon: "house", items: [
    ["members:categories"],
    ["members:member-settings"],
  ] },
  { id: "golf", labelKey: "menu.settings.golf", icon: "golf", items: [
    ["teesheet:courses"],
    ["teesheet:templates"],
    ["teesheet:rules"],
    ["guests:policy"],
  ] },
  { id: "pos", labelKey: "menu.settings.pos", icon: "tag", items: [
    ["pos:pos-outlets"],
    ["pos:pos-categories"],
    ["pos:pos-modifiers"],
    ["pos:pos-reasons", "menu.voidCompReasons"],
    ["pos:pos-devices"],
    ["events:dining-setup"],
    ["events:setup"],
  ] },
  { id: "rooms", labelKey: "menu.settings.rooms", icon: "calendar", items: [
    ["lodging:units"],
    ["lodging:setup"],
    ["lodging:rates"],
    ["lodging:rules", "menu.bookingRules"],
    ["lodging:housekeeping-setup"],
  ] },
  { id: "finance", labelKey: "menu.settings.finance", icon: "card", items: [
    ["ledger:ledger-accounts"],
    ["ledger:ledger-calendar", "menu.accountingPeriods"],
    ["ledger:ledger-departments", "menu.departments"],
    ["ledger:ledger-rules", "menu.accountMapping"],
    ["ledger:ledger-bridge", "menu.billingToLedger"],
    ["ledger:ledger-tie-outs", "menu.reconciliations"],
    ["ledger:ledger-deferrals"],
    ["ledger:ledger-settings"],
    ["billing:billing-settings"],
  ] },
  { id: "communications", labelKey: "menu.settings.communications", icon: "message", items: [
    ["communications:settings"],
    ["admin:communications"],
    ["notifications:notifications-templates"],
  ] },
  { id: "system", labelKey: "menu.settings.system", icon: "key", items: [
    ["admin:installation"],
  ] },
  { id: "migration", labelKey: "menu.settings.migration", icon: "download", items: [
    ["importers:migration-readiness"],
    ["importers:import-runs"],
    ["importers:history-reconciliation"],
    ["members:range-plan", "menu.rangePlanAudit"],
    ["accounting:accounting-jonas"],
    ["pos:pos-jonas"],
    ["lodging:jonas"],
  ] },
];
